/* eslint-disable max-lines-per-function */

import { Checkbox, Flex, Group, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';

import { SortDirection, TemplateType, TransactionTypes } from '@e-origin/shared';

import { useDeclarants, useTransactionTypes } from '@admin/hooks';
import { MultiSelect, Select, TextInput } from '@components';
import { templateViewAllOptions } from '../ManageTemplates';

export const GeneralInfo = ({ templateType, handleChange, form, editDisabled }) => {
  const { data: declarantsData } = useDeclarants();
  const { data: { transactionTypes = [] } = {} } = useTransactionTypes({
    filters: {},
    sorting: { field: 'name', direction: SortDirection.ASC },
    pagination: { page: 1, size: 100000 },
  });

  const extractTypeLabel = useMemo(() => {
    return templateViewAllOptions.find((opt) => opt.value === templateType)?.label || '';
  }, [templateType]);

  const type = useMemo(() => {
    return [TemplateType.HIGH_VALUE_H1, TemplateType.LOW_VALUE_H7, TemplateType.ARRIVAL_TRANSIT, TemplateType.DEPARTURE_TRANSIT].includes(templateType)
      ? templateType
      : 'other';
  }, [templateType]);

  const transactionTypeOptions =
    transactionTypes?.map((transactionType) => ({
      label: transactionType.name || '',
      value: transactionType._id || '',
    })) || [];

  const handleTransactionTypeChange = (value: string) => {
    form.setFieldValue('transactionType', value);
    handleChange({ ...form.values, transactionType: value });
  };

  const declarantsSelectData =
    declarantsData?.map((declarant) => ({
      value: declarant._id,
      label: declarant.accountName || declarant.name || 'N/A',
    })) || [];

  const selectedDeclarantValues = (form.values.declarants || []).map((id: string) => declarantsSelectData.find((item) => item.value === id)?.label ?? id);

  const handleDeclarantsChange = (selectedLabels: string[]) => {
    const selectedIds = selectedLabels.map(
      (labelOrId) => declarantsSelectData.find((item) => item.label === labelOrId || item.value === labelOrId)?.value ?? labelOrId,
    );

    form.setFieldValue('declarants', selectedIds);
    handleChange({ ...form.values, declarants: selectedIds });
  };

  return (
    <Stack gap="xl">
      <Group align="center" gap="xl">
        <Stack gap="xs">
          <Text c="gray" size="sm">
            Type
          </Text>
          <Text fw={500}>{extractTypeLabel}</Text>
        </Stack>

        <TextInput
          value={form.values.name}
          onChange={(event) => {
            handleChange({ ...form.values, name: event.target.value });
          }}
          label="Name"
          w={350}
          disabled={editDisabled}
        />

        <Select
          value={form.values.transactionType}
          onChange={handleTransactionTypeChange}
          label="Select transaction type"
          data={transactionTypeOptions}
          searchable
          size="lg"
          disabled={editDisabled}
        />

        <Checkbox
          {...form.getInputProps('deleted', { type: 'checkbox' })}
          label="Active"
          checked={!form.values.deleted}
          onChange={(event) => handleChange({ ...form.values, deleted: !event.currentTarget.checked })}
          disabled={editDisabled}
        />
      </Group>

      <MultiSelect
        label="Declarants"
        data={declarantsSelectData}
        value={selectedDeclarantValues}
        onChange={handleDeclarantsChange}
        fetchOptions={async () => declarantsSelectData}
        miw={600}
        w="70%"
        disabled={editDisabled}
      />

      {type === TemplateType.HIGH_VALUE_H1 && (
        <Flex gap="md" wrap="wrap">
          <Checkbox
            {...form.getInputProps('groupByShippingMark', { type: 'checkbox' })}
            label="Group by shipping mark"
            checked={form.values.groupByShippingMark}
            onChange={(event) => handleChange({ ...form.values, groupByShippingMark: event.currentTarget.checked })}
            disabled={editDisabled}
          />

          <Checkbox
            {...form.getInputProps('groupLRNByShippingMarks', { type: 'checkbox' })}
            label="Group LRN by shipping marks"
            checked={form.values.groupLRNByShippingMarks}
            onChange={(event) => handleChange({ ...form.values, groupLRNByShippingMarks: event.currentTarget.checked })}
            disabled={editDisabled}
          />

          <Checkbox
            {...form.getInputProps('groupByOrigin', { type: 'checkbox' })}
            label="Group by origin"
            checked={form.values.groupByOrigin}
            onChange={(event) => handleChange({ ...form.values, groupByOrigin: event.currentTarget.checked })}
            disabled={editDisabled}
          />

          <Checkbox
            {...form.getInputProps('groupByTrackingId', { type: 'checkbox' })}
            label="Group by tracking id"
            checked={form.values.groupByTrackingId}
            onChange={(event) => handleChange({ ...form.values, groupByTrackingId: event.currentTarget.checked })}
            disabled={editDisabled}
          />

          <Checkbox
            {...form.getInputProps('isExport', { type: 'checkbox' })}
            label="Is export"
            checked={form.values.isExport}
            onChange={(event) => handleChange({ ...form.values, isExport: event.currentTarget.checked })}
            disabled={editDisabled}
          />
        </Flex>
      )}

      {type === TemplateType.LOW_VALUE_H7 && (
        <Flex gap="md" wrap="wrap" align="center">
          <TextInput
            value={form.values.sheetName}
            onChange={(event) => {
              handleChange({ ...form.values, sheetName: event.target.value });
            }}
            label="Sheet name"
            w={500}
            disabled={editDisabled}
          />

          <Checkbox
            {...form.getInputProps('skipSplitBatchForSplitShipments', { type: 'checkbox' })}
            label="Skip split batch for split shipments"
            checked={form.values.skipSplitBatchForSplitShipments}
            onChange={(event) => handleChange({ ...form.values, skipSplitBatchForSplitShipments: event.currentTarget.checked })}
            disabled={editDisabled}
          />
        </Flex>
      )}

      {([TransactionTypes.B2B_HV, TransactionTypes.B2B_EXPORT].includes(transactionTypes.find((tt) => tt._id === form.values.transactionType)?.typeId) ||
        type === 'other') && (
        <Flex gap="md" wrap="wrap">
          <TextInput
            value={form.values.templateUrl}
            onChange={(event) => {
              form.setFieldValue('templateUrl', event.target.value);
              handleChange({ ...form.values, templateUrl: event.target.value });
            }}
            label="Template URL"
            w={500}
            disabled={editDisabled}
          />

          {type === 'other' && (
            <TextInput
              value={form.values.templateDynamicPageUrl}
              onChange={(event) => {
                handleChange({ ...form.values, templateDynamicPageUrl: event.target.value });
              }}
              label="Template dynamic page URL"
              w={500}
              disabled={editDisabled}
            />
          )}
        </Flex>
      )}

      {type === 'other' && (
        <Flex gap="md" wrap="wrap">
          <TextInput
            value={form.values.templateWatermarkUrl}
            onChange={(event) => {
              handleChange({ ...form.values, templateWatermarkUrl: event.target.value });
            }}
            label="Template watermark URL"
            w={500}
            disabled={editDisabled}
          />

          <TextInput
            value={form.values.templateWatermarkDynamicPageUrl}
            onChange={(event) => {
              handleChange({ ...form.values, templateWatermarkDynamicPageUrl: event.target.value });
            }}
            label="Template watermark dynamic page URL"
            w={500}
            disabled={editDisabled}
          />
        </Flex>
      )}
    </Stack>
  );
};
